import type { InstaQLEntity } from "@instantdb/admin";
import { empty } from "gamla";
import { useEffect, useRef, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import {
  apiClient,
  dashboardCreateTaskEndpoint,
  dashboardGetGroupsEndpoint,
} from "../../constants.ts";
import { preferredNetworkValues } from "../../scheduledActionSchema.ts";
import schema from "../../instant.schema.ts";
import { Button, InfiniteScrollTrigger, PageSpinner } from "./components.tsx";
import { transact, tx, useAuth, useInfiniteQuery } from "./db.ts";

type Task = InstaQLEntity<typeof schema, "tasks">;

const inputStyle =
  "w-full border border-border-subtle-light dark:border-border-subtle rounded-lg px-3 py-2 bg-input-light dark:bg-input text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors font-body";

type PreferredNetwork = typeof preferredNetworkValues[number];

const isPreferredNetwork = (v: string): v is PreferredNetwork =>
  (preferredNetworkValues as readonly string[]).includes(v);

const networkLabels: Record<PreferredNetwork, string> = {
  "whatsapp": "WhatsApp",
  "whatsapp-demo": "WhatsApp Demo",
  "telegram-demo": "Telegram Demo",
  "whatsapp-demo-business-integration": "WhatsApp Official Demo",
  "email": "Email",
  "phone-call": "Phone Call",
  "slack": "Slack",
  "sms": "SMS",
  "teams": "Teams",
  "telegram": "Telegram",
  "alice-and-bot": "Alice&Bot",
  "facebook-messenger": "Facebook Messenger",
  "instagram": "Instagram",
  "whatsapp-for-business": "WhatsApp Business",
};

const targetFieldLabel: Record<PreferredNetwork, string> = {
  "whatsapp": "Phone or WhatsApp group",
  "whatsapp-demo": "Phone or WhatsApp group",
  "telegram-demo": "Telegram chat ID",
  "whatsapp-demo-business-integration": "Phone or WhatsApp group",
  "email": "Email address",
  "phone-call": "Phone number",
  "slack": "Slack user or conversation ID",
  "sms": "Phone number",
  "teams": "Teams user ID",
  "telegram": "Telegram user ID",
  "alice-and-bot": "Conversation ID",
  "facebook-messenger": "Messenger conversation ID",
  "instagram": "Instagram user ID",
  "whatsapp-for-business": "Phone number",
};

const targetFieldPlaceholder: Record<PreferredNetwork, string> = {
  "whatsapp": "e.g. 972501234567 or select a group",
  "whatsapp-demo": "e.g. 972501234567@c.us",
  "telegram-demo": "e.g. 123456789",
  "whatsapp-demo-business-integration": "e.g. 972501234567@c.us",
  "email": "e.g. name@example.com",
  "phone-call": "e.g. +1234567890",
  "slack": "e.g. U12345678 or D12345678",
  "sms": "e.g. +1234567890",
  "teams": "e.g. 29:1abcd...",
  "telegram": "e.g. 123456789",
  "alice-and-bot": "e.g. conversation-id",
  "facebook-messenger": "e.g. conversation-id",
  "instagram": "e.g. user-id",
  "whatsapp-for-business": "e.g. 972501234567",
};

const normalizeWhatsappTarget = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.endsWith("@g.us") || trimmed.endsWith("@c.us")) return trimmed;
  const digits = trimmed.replace(/[^0-9]/g, "");
  return digits ? `${digits}@c.us` : trimmed;
};

type WhatsAppGroup = { id: string; name: string; isAdmin: boolean };

type GroupsResponse = {
  groups: WhatsAppGroup[];
  warning: string;
  cacheMaxAgeSeconds: number;
};

type RecurrenceFilter = "all" | "recurring" | "one-time";

const isRecurrenceFilter = (value: string): value is RecurrenceFilter =>
  value === "all" || value === "recurring" || value === "one-time";

const fetchGroups = (
  token: string,
  botId: string,
  useCache?: boolean,
) =>
  apiClient({
    endpoint: dashboardGetGroupsEndpoint,
    token,
    payload: {
      botId,
      ...(useCache === undefined ? {} : { useCache }),
    },
  }).then((response: GroupsResponse) => response);

const matchesFilter = (filter: string) => (g: WhatsAppGroup) =>
  g.name.toLowerCase().includes(filter.toLowerCase()) ||
  g.id.toLowerCase().includes(filter.toLowerCase());

const isRecurringTask = (task: Task) => Boolean(task.recurrenceRule);

const byRecurrence = (recurrenceFilter: RecurrenceFilter) => (task: Task) =>
  recurrenceFilter === "all"
    ? true
    : recurrenceFilter === "recurring"
    ? isRecurringTask(task)
    : !isRecurringTask(task);

const byTargetOrGroup = (
  targetFilter: string,
  groupNameById: Record<string, string>,
) =>
(task: Task) => {
  const query = targetFilter.trim().toLowerCase();
  if (!query) return true;
  const targetId = (task.targetConversationId ?? "").toLowerCase();
  const groupName = (groupNameById[task.targetConversationId ?? ""] ?? "")
    .toLowerCase();
  return targetId.includes(query) || groupName.includes(query);
};

const copyTaskId = (id: string) =>
  navigator.clipboard.writeText(id).then(
    () => toast.success("Task ID copied"),
    () => toast.error("Failed to copy task ID"),
  );

const GroupAutocomplete = (
  { groups, value, onSelect, disabled, className }: {
    groups: WhatsAppGroup[];
    value: string;
    onSelect: (id: string) => void;
    disabled: boolean;
    className: string;
  },
) => {
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = groups.find((g) => g.id === value);
  const filtered = filter ? groups.filter(matchesFilter(filter)) : groups;

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        placeholder={selected ? `${selected.name}` : "Search groups..."}
        value={open ? filter : selected ? selected.name : filter}
        onFocus={() => setOpen(true)}
        onInput={({ currentTarget: { value: v } }) => {
          setFilter(v);
          setOpen(true);
        }}
        className={className}
        disabled={disabled}
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised shadow-lg">
          {empty(filtered)
            ? (
              <div className="px-3 py-2 text-xs text-text-secondary-light dark:text-text-secondary">
                No groups found
              </div>
            )
            : filtered.map((g) => (
              <button
                key={g.id}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-surface-overlay-light dark:hover:bg-surface-overlay cursor-pointer flex items-center gap-2"
                onClick={() => {
                  onSelect(g.id);
                  setFilter("");
                  setOpen(false);
                }}
              >
                <span className="truncate">{g.name}</span>
                {g.isAdmin && (
                  <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-accent-light/10 dark:bg-accent/10 text-accent-light dark:text-accent">
                    admin
                  </span>
                )}
                <span className="ml-auto text-[10px] text-text-secondary-light dark:text-text-secondary truncate">
                  {g.id}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

const TruncatedText = (
  { text, limit = 80 }: { text: string; limit?: number },
) => {
  const [expanded, setExpanded] = useState(false);
  if (!text || text.length <= limit) return <span>{text}</span>;
  return (
    <span>
      {expanded ? text : `${text.slice(0, limit)}...`}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="ml-1 text-accent-light dark:text-accent text-xs hover:underline"
      >
        {expanded ? "show less" : "show more"}
      </button>
    </span>
  );
};

const defaultIconClassName = "w-5 h-5 shrink-0";

const CheckCircleIcon = (
  { className = defaultIconClassName }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const RunningSpinnerIcon = (
  { className = defaultIconClassName }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const ClockIcon = (
  { className = defaultIconClassName }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CircleSlashIcon = (
  { className = defaultIconClassName }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
);

const SkipForwardIcon = (
  { className = defaultIconClassName }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="5 4 15 12 5 20 5 4" />
    <line x1="19" y1="5" x2="19" y2="19" />
  </svg>
);

const AlertCircleIcon = (
  { className = defaultIconClassName }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const InfoCircleIcon = (
  { className = defaultIconClassName }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export type TaskStatusCategory =
  | "done"
  | "running"
  | "failed"
  | "cancelled"
  | "skipped"
  | "waiting"
  | "unknown";

export const getTaskStatusCategory = (status: string): TaskStatusCategory => {
  const normalized = (status || "").toLowerCase();
  if (
    normalized === "done" ||
    normalized === "completed" ||
    normalized === "success"
  ) {
    return "done";
  }
  if (normalized.startsWith("running")) {
    return "running";
  }
  if (normalized.includes("failed") || normalized.includes("error")) {
    return "failed";
  }
  if (normalized === "cancelled") {
    return "cancelled";
  }
  if (normalized.startsWith("skipped")) {
    return "skipped";
  }
  if (
    normalized === "waiting to run" ||
    normalized.includes("scheduled") ||
    normalized === "pending"
  ) {
    return "waiting";
  }
  return "unknown";
};

export const TaskStatusIcon = ({ status }: { status: string }) => {
  const category = getTaskStatusCategory(status);
  if (category === "done") {
    return (
      <span
        title={status}
        className="inline-flex items-center text-emerald-500 dark:text-emerald-400"
      >
        <CheckCircleIcon className={defaultIconClassName} />
        <span className="sr-only">{status}</span>
      </span>
    );
  }
  if (category === "running") {
    return (
      <span
        title={status}
        className="inline-flex items-center text-blue-500 dark:text-blue-400"
      >
        <RunningSpinnerIcon
          className={`${defaultIconClassName} animate-spin`}
        />
        <span className="sr-only">{status}</span>
      </span>
    );
  }
  if (category === "failed") {
    return (
      <span
        title={status}
        className="inline-flex items-center text-rose-500 dark:text-rose-400"
      >
        <AlertCircleIcon className={defaultIconClassName} />
        <span className="sr-only">{status}</span>
      </span>
    );
  }
  if (category === "cancelled") {
    return (
      <span
        title={status}
        className="inline-flex items-center text-text-secondary-light dark:text-text-secondary opacity-60"
      >
        <CircleSlashIcon className={defaultIconClassName} />
        <span className="sr-only">{status}</span>
      </span>
    );
  }
  if (category === "skipped") {
    return (
      <span
        title={status}
        className="inline-flex items-center text-indigo-500 dark:text-indigo-400"
      >
        <SkipForwardIcon className={defaultIconClassName} />
        <span className="sr-only">{status}</span>
      </span>
    );
  }
  if (category === "waiting") {
    return (
      <span
        title={status}
        className="inline-flex items-center text-amber-500 dark:text-amber-400"
      >
        <ClockIcon className={defaultIconClassName} />
        <span className="sr-only">{status}</span>
      </span>
    );
  }
  return (
    <span
      title={status}
      className="inline-flex items-center text-text-secondary-light dark:text-text-secondary"
    >
      <InfoCircleIcon className={defaultIconClassName} />
      <span className="sr-only">{status}</span>
    </span>
  );
};

const renderTable = (onResolve: (task: Task) => void) => (tasks: Task[]) => (
  <div className="overflow-x-auto rounded-lg bg-input-light dark:bg-input">
    <table className="min-w-full divide-y divide-border-subtle-light dark:divide-border-subtle">
      <thead>
        <tr>
          <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
            Target conversation
          </th>
          <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
            Description
          </th>
          <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
            Status
          </th>
          <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
            Run At
          </th>
          <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
            Recurring
          </th>
          <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
            Preferred Network
          </th>
          <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {tasks.map(renderTaskRow(onResolve))}
      </tbody>
    </table>
  </div>
);

const renderTaskRow = (onResolve: (task: Task) => void) => (task: Task) => (
  <tr
    key={task.id}
    className="border-b border-border-subtle-light dark:border-border-subtle"
  >
    <td className="px-4 py-2">{task.targetConversationId}</td>
    <td className="px-4 py-2">
      <TruncatedText text={task.description} />
    </td>
    <td className="px-4 py-2">
      <TaskStatusIcon status={task.status} />
    </td>
    <td className="px-4 py-2">
      {task.runAt ? new Date(task.runAt).toLocaleString() : ""}
    </td>
    <td className="px-4 py-2">{task.recurrenceRule || "-"}</td>
    <td className="px-4 py-2">{task.preferredNetwork}</td>
    <td className="px-4 py-2 flex gap-2">
      <Button
        type="button"
        className="text-xs px-2 py-1"
        onClick={() => {
          void copyTaskId(task.id);
        }}
      >
        Copy ID
      </Button>
      {!task.resolved && (
        <Button onClick={() => onResolve(task)} className="text-xs px-2 py-1">
          Resolve
        </Button>
      )}
    </td>
  </tr>
);

const handleResolve = (task: Task) =>
  toast.promise(
    transact([tx.tasks[task.id].update({ resolved: true, status: "Done" })]),
    {
      loading: "Resolving...",
      success: "Task resolved",
      error: () => "Failed to resolve task",
    },
  );

const submitCreateTask = async (
  {
    token,
    botId,
    description,
    runAt,
    preferredNetwork,
    targetConversationId,
    recurrenceRule,
    recurrenceEnd,
    subject,
  }: {
    token: string;
    botId: string;
    description: string;
    runAt: string;
    preferredNetwork: PreferredNetwork;
    targetConversationId: string;
    recurrenceRule: string;
    recurrenceEnd: string;
    subject: string;
  },
) => {
  const res = await apiClient({
    endpoint: dashboardCreateTaskEndpoint,
    token,
    payload: {
      botId,
      description,
      preferredNetwork,
      ...(runAt ? { runAt } : {}),
      ...(targetConversationId ? { targetConversationId } : {}),
      ...(recurrenceRule ? { recurrenceRule } : {}),
      ...(recurrenceEnd ? { recurrenceEnd } : {}),
      ...(subject ? { subject } : {}),
    },
  });
  if (!res.success) throw new Error(res.error || "Unknown error");
  return res;
};

const CreateTaskForm = (
  { botId, token }: { botId: string; token: string },
) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");
  const [runAt, setRunAt] = useState("");
  const [network, setNetwork] = useState<PreferredNetwork>(
    preferredNetworkValues[0],
  );
  const [targetConversationId, setTargetConversationId] = useState("");
  const [recurrenceRule, setRecurrenceRule] = useState("");
  const [recurrenceEnd, setRecurrenceEnd] = useState("");
  const [subject, setSubject] = useState("");
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsWarning, setGroupsWarning] = useState<string | null>(null);

  useEffect(() => {
    if (network !== "whatsapp" || !open) return;
    if (groups.length > 0) return;
    setGroupsLoading(true);
    fetchGroups(token, botId)
      .then(({ groups, warning }) => {
        setGroups(groups);
        setGroupsWarning(warning);
      })
      .catch(() => {})
      .finally(() => setGroupsLoading(false));
  }, [network, open]);

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} className="mb-4">
        Add task
      </Button>
    );
  }

  const resetForm = () => {
    setDescription("");
    setRunAt("");
    setNetwork(preferredNetworkValues[0]);
    setTargetConversationId("");
    setRecurrenceRule("");
    setRecurrenceEnd("");
    setSubject("");
  };

  const handleSubmit = () => {
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }
    setLoading(true);
    toast.promise(
      submitCreateTask({
        token,
        botId,
        description: description.trim(),
        runAt,
        preferredNetwork: network,
        targetConversationId: network === "whatsapp"
          ? normalizeWhatsappTarget(targetConversationId)
          : targetConversationId,
        recurrenceRule,
        recurrenceEnd,
        subject,
      }).then(() => {
        resetForm();
        setOpen(false);
      }).finally(() => setLoading(false)),
      {
        loading: "Creating task...",
        success: "Task created",
        error: (err) => err.message || "Failed to create task",
      },
    );
  };

  return (
    <div className="mb-4 p-4 border border-border-subtle-light dark:border-border-subtle rounded-lg space-y-3">
      <div className="text-sm font-medium">New Task</div>
      <textarea
        placeholder="Description (what the bot should do)"
        value={description}
        onInput={({ currentTarget: { value } }) => setDescription(value)}
        className={inputStyle + " min-h-[60px]"}
        disabled={loading}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-text-secondary-light dark:text-text-secondary mb-1">
            Network
          </label>
          <select
            className={inputStyle}
            value={network}
            onChange={({ currentTarget: { value } }) =>
              isPreferredNetwork(value) && setNetwork(value)}
            disabled={loading}
          >
            {preferredNetworkValues.map((n) => (
              <option key={n} value={n}>{networkLabels[n]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-text-secondary-light dark:text-text-secondary mb-1">
            Run at (local time, empty for ASAP)
          </label>
          <input
            type="datetime-local"
            value={runAt}
            onInput={({ currentTarget: { value } }) => setRunAt(value)}
            className={inputStyle}
            disabled={loading}
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary-light dark:text-text-secondary mb-1">
            {targetFieldLabel[network]}
          </label>
          {network === "whatsapp" && (groups.length > 0 || groupsLoading)
            ? groupsLoading
              ? (
                <div
                  className={inputStyle +
                    " flex items-center text-text-secondary-light dark:text-text-secondary text-sm"}
                >
                  Loading groups...
                </div>
              )
              : (
                <GroupAutocomplete
                  groups={groups}
                  value={targetConversationId}
                  onSelect={setTargetConversationId}
                  disabled={loading}
                  className={inputStyle}
                />
              )
            : (
              <input
                type="text"
                placeholder={targetFieldPlaceholder[network]}
                value={targetConversationId}
                onInput={({ currentTarget: { value } }) =>
                  setTargetConversationId(value)}
                className={inputStyle}
                disabled={loading}
              />
            )}
        </div>
        <div>
          <label className="block text-xs text-text-secondary-light dark:text-text-secondary mb-1">
            Recurrence (optional)
          </label>
          <input
            type="text"
            placeholder="daily, weekly, monthly, or RRULE:..."
            value={recurrenceRule}
            onInput={({ currentTarget: { value } }) => setRecurrenceRule(value)}
            className={inputStyle}
            disabled={loading}
          />
        </div>
        {recurrenceRule && (
          <div>
            <label className="block text-xs text-text-secondary-light dark:text-text-secondary mb-1">
              Recurrence end (optional)
            </label>
            <input
              type="datetime-local"
              value={recurrenceEnd}
              onInput={({ currentTarget: { value } }) =>
                setRecurrenceEnd(value)}
              className={inputStyle}
              disabled={loading}
            />
          </div>
        )}
        {network === "email" && (
          <div>
            <label className="block text-xs text-text-secondary-light dark:text-text-secondary mb-1">
              Email subject
            </label>
            <input
              type="text"
              placeholder="Subject line"
              value={subject}
              onInput={({ currentTarget: { value } }) => setSubject(value)}
              className={inputStyle}
              disabled={loading}
            />
          </div>
        )}
      </div>
      {network === "whatsapp" && groupsWarning && (
        <div className="text-xs text-text-secondary-light dark:text-text-secondary">
          {groupsWarning}
        </div>
      )}
      <div className="flex gap-2">
        <Button onClick={handleSubmit} disabled={loading}>
          Create
        </Button>
        <Button
          onClick={() => {
            resetForm();
            setOpen(false);
          }}
          disabled={loading}
          className="!bg-transparent !text-text-secondary-light dark:!text-text-secondary !shadow-none hover:!bg-surface-overlay-light dark:hover:!bg-surface-overlay"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

const tasksPageSize = 25;

export const Tasks = ({ id }: { id: string }) => {
  const token = useAuth().user?.refresh_token ?? "";
  const [showResolved, setShowResolved] = useState(false);
  const [targetFilter, setTargetFilter] = useState("");
  const [recurrenceFilter, setRecurrenceFilter] = useState<RecurrenceFilter>(
    "all",
  );
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [groupsWarning, setGroupsWarning] = useState<string | null>(null);
  const whereClause = showResolved
    ? { "bot.id": id }
    : { "bot.id": id, resolved: false };
  const {
    data: tasksData,
    isLoading,
    error,
    canLoadNextPage,
    loadNextPage,
  } = useInfiniteQuery(
    id
      ? {
        tasks: {
          $: {
            where: whereClause,
            limit: tasksPageSize,
            order: { runAt: "desc" },
          },
        },
      }
      : null,
  );

  useEffect(() => {
    if (!id || !token) return;
    fetchGroups(token, id)
      .then(({ groups, warning }) => {
        setGroups(groups);
        setGroupsWarning(warning);
      })
      .catch(() => {
        setGroups([]);
        setGroupsWarning(null);
      });
  }, [id, token]);

  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error loading tasks: {error.message}</div>;
  const tasks = tasksData?.tasks ?? [];
  const groupNameById = Object.fromEntries(groups.map((g) => [g.id, g.name]));
  const filteredTasks = tasks
    .filter(byRecurrence(recurrenceFilter))
    .filter(byTargetOrGroup(targetFilter, groupNameById));
  return (
    <div>
      <div className="mb-4 space-y-2 text-sm text-text-secondary-light dark:text-text-secondary">
        <p>
          Manage scheduled tasks for this bot. For instructions on how to create
          tasks programmatically, see the{" "}
          <a
            href="/docs"
            className="text-accent-light dark:text-accent hover:underline"
          >
            API Documentation
          </a>.
        </p>
        {groupsWarning && <p>{groupsWarning}</p>}
      </div>
      <CreateTaskForm botId={id} token={token} />
      <label className="flex items-center gap-2 mb-2">
        <input
          type="checkbox"
          checked={showResolved}
          onChange={() => setShowResolved((v) => !v)}
        />
        Show resolved tasks
      </label>
      <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text"
          value={targetFilter}
          onInput={({ currentTarget: { value } }) => setTargetFilter(value)}
          placeholder="Filter by contact ID (e.g. phone) or WhatsApp group name"
          className={inputStyle}
        />
        <select
          value={recurrenceFilter}
          onChange={({ currentTarget: { value } }) => {
            if (isRecurrenceFilter(value)) setRecurrenceFilter(value);
          }}
          className={inputStyle}
        >
          <option value="all">All tasks</option>
          <option value="recurring">Recurring only</option>
          <option value="one-time">One-time only</option>
        </select>
      </div>
      <div className="max-h-[600px] overflow-y-auto">
        {empty(filteredTasks)
          ? <div>The bot's task list is empty.</div>
          : renderTable(handleResolve)(filteredTasks)}
        {canLoadNextPage && (
          <InfiniteScrollTrigger
            onIntersect={loadNextPage}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
};
